import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../lib/config/theme';
import { WaypointMark } from '../brand/WaypointMark';
import { hashTarget, MARKETING_HEADER_OFFSET } from './useMarketingScroll';

// Every link is a router <Link>, including "/#product": a plain <a> reloads
// the whole app when clicked from /pricing, and skips the scroll handling in
// useMarketingScroll. Waypoint is one platform, so "Product" is singular; it
// points at the home page section, and Owner and Sales have their own pages.
// Clients is not sold self-serve (2026-11-02), so it has no link.
const NAV_LINKS = [
  { label: 'Product', href: '/#product' },
  { label: 'Owner', href: '/platform/owner' },
  { label: 'Sales', href: '/platform/sales' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Free Report', href: '/instant-report' },
  { label: 'Demo', href: '/demo' },
] as const;

type NavLink = (typeof NAV_LINKS)[number];

export function MarketingNav() {
  const { theme, toggle: toggleTheme } = useTheme();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [sectionInView, setSectionInView] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onHome = location.pathname === '/';
    function onScroll() {
      setScrolled(window.scrollY > 80);
      // Light up a section link (Product) while that section sits under the
      // nav, so the highlight follows the visitor rather than the last click.
      let inView: string | null = null;
      if (onHome) {
        for (const link of NAV_LINKS) {
          if (!link.href.startsWith('/#')) continue;
          const rect = hashTarget(link.href.slice(1))?.getBoundingClientRect();
          if (rect && rect.top <= MARKETING_HEADER_OFFSET + 1 && rect.bottom > MARKETING_HEADER_OFFSET) inView = link.href;
        }
      }
      setSectionInView(inView);
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname]);

  const isActive = (link: NavLink) =>
    link.href.startsWith('/#') ? sectionInView === link.href : location.pathname === link.href;

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Close on Escape
  useEffect(() => {
    if (!mobileOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMobileOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  // Close on outside click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    },
    [],
  );

  return (
    <header className="fixed left-0 right-0 top-0 z-modal flex justify-center px-4 pt-4 md:pt-6">
      <nav
        ref={menuRef}
        className={`relative inline-flex flex-col rounded-2xl border backdrop-blur-xl transition-all duration-300 md:flex-row md:items-center md:rounded-full ${
          scrolled
            ? 'border-border bg-surface-0/90 shadow-lift'
            : 'border-white/6 bg-surface-0/60'
        }`}
      >
        {/* ─── Top bar ─── */}
        <div className="flex items-center gap-1 px-3 py-2">
          {/* Logo */}
          <Link to="/" className="mr-2 flex items-center gap-2 rounded-full px-2 py-1">
            <WaypointMark size={28} />
            <span className="text-[13px] font-semibold tracking-tight text-ink">Waypoint</span>
          </Link>

          <div className="hidden h-5 w-px bg-border sm:block" />

          {/* Desktop nav links */}
          {NAV_LINKS.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              aria-current={isActive(link) ? 'page' : undefined}
              className={`hidden rounded-full px-3 py-1.5 text-[13px] transition-colors hover:bg-surface-2 hover:text-ink sm:block ${
                isActive(link) ? 'bg-surface-2 text-ink' : 'text-ink-muted'
              }`}
            >
              {link.label}
            </Link>
          ))}

          <div className="hidden h-5 w-px bg-border sm:block" />

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="flex h-8 w-8 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink"
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
              </svg>
            )}
          </button>

          {/* Desktop auth links */}
          <Link
            to="/login"
            className="hidden rounded-full px-3 py-1.5 text-[13px] text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink sm:block"
          >
            Sign in
          </Link>
          <Link
            to="/signup"
            className="group relative ml-1 hidden items-center gap-1.5 rounded-full px-4 py-1.5 text-[13px] font-medium sm:inline-flex"
          >
            <span className="absolute inset-0 rounded-full border border-primary bg-primary transition-all group-hover:shadow-glow" />
            <span className="relative text-white">Get started</span>
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              className="relative text-white/80 transition-transform group-hover:translate-x-0.5"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="ml-auto flex h-8 w-8 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink sm:hidden"
            aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="4" y1="7" x2="20" y2="7" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="17" x2="20" y2="17" />
              </svg>
            )}
          </button>
        </div>

        {/* ─── Mobile panel ─── */}
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out sm:hidden ${
            mobileOpen ? 'max-h-[32rem] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="flex flex-col gap-1 border-t border-border px-4 pb-4 pt-3">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                aria-current={isActive(link) ? 'page' : undefined}
                className={`rounded-lg px-3 py-2 text-[14px] transition-colors hover:bg-surface-2 hover:text-ink ${
                  isActive(link) ? 'bg-surface-2 text-ink' : 'text-ink-muted'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="my-2 h-px bg-border" />
            <Link
              to="/login"
              onClick={() => setMobileOpen(false)}
              className="rounded-lg px-3 py-2 text-[14px] text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink"
            >
              Sign in
            </Link>
            <Link
              to="/signup"
              onClick={() => setMobileOpen(false)}
              className="mt-1 inline-flex items-center justify-center gap-2 rounded-xl border border-primary bg-primary px-4 py-2.5 text-[14px] font-semibold text-white transition-all hover:brightness-110"
            >
              Get started
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </nav>

      {/* Backdrop overlay for mobile menu */}
      {mobileOpen && (
        <div
          className="fixed inset-0 -z-10 bg-black/30 backdrop-blur-xs sm:hidden"
          onClick={handleBackdropClick}
          aria-hidden="true"
        />
      )}
    </header>
  );
}
